
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../api/api'
import axios from 'axios';
import { api_url } from '../../utils/utils'
import { paginationTotal } from '../../utils/pagination'

// ✅ Get all digital products
export const get_admin_digital_products = createAsyncThunk(
    'adminDigitalProduct/get_admin_digital_products',
    async ({ page, perPage, searchValue, category, status }, { rejectWithValue, fulfillWithValue, getState }) => {
        try {
            const params = new URLSearchParams({ page, perPage })
            if (searchValue) params.append('searchValue', searchValue)
            if (category) params.append('category', category)
            if (status) params.append('status', status)
            const { data } = await axios.get(`${api_url}/api/admin/digital-products?${params.toString()}`, {
                withCredentials: true
            })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

// ✅ Get single digital product
export const get_admin_digital_product = createAsyncThunk(
    'adminDigitalProduct/get_admin_digital_product',
    async (productId, { rejectWithValue, fulfillWithValue, getState }) => {
        try {
            const { data } = await axios.get(`${api_url}/api/admin/digital-products/${productId}`, {
                withCredentials: true
            })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

// ✅ Add digital product - FormData ilə
export const add_admin_digital_product = createAsyncThunk(
    'adminDigitalProduct/add_admin_digital_product',
    async (formData, { rejectWithValue, fulfillWithValue, getState }) => {
        try {
            const { data } = await api.post(`/admin/digital-products`, formData, {
              withCredentials: true,
            })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

// ✅ Update digital product - FormData ilə
export const update_admin_digital_product = createAsyncThunk(
    'adminDigitalProduct/update_admin_digital_product',
    async ({ productId, formData }, { rejectWithValue, fulfillWithValue, getState }) => {
        try {
            const { data } = await api.put(`/admin/digital-products/${productId}`, formData, {
                withCredentials: true
            })
            
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

// ✅ Delete digital product
export const delete_admin_digital_product = createAsyncThunk(
    'adminDigitalProduct/delete_admin_digital_product',
    async (productId, { rejectWithValue, fulfillWithValue, getState }) => {
        try {
            const { data } = await api.delete(`/admin/digital-products/${productId}`, {
                withCredentials: true
            })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

// ✅ Get digital orders
export const get_admin_digital_orders = createAsyncThunk(
    'adminDigitalProduct/get_admin_digital_orders',
    async ({ page, perPage, status }, { rejectWithValue, fulfillWithValue, getState }) => {
        try {
            const params = new URLSearchParams({ page, perPage })
            if (status) params.append('status', status)
            const { data } = await axios.get(`${api_url}/api/admin/digital-orders?${params.toString()}`, {
                withCredentials: true
            })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

// ✅ Update order status
export const update_digital_order_status = createAsyncThunk(
    'adminDigitalProduct/update_digital_order_status',
    async ({ orderId, updateData }, { rejectWithValue, fulfillWithValue, getState }) => {
        try {
            const { data } = await api.put(`/admin/digital-orders/${orderId}`, updateData, {
                withCredentials: true
            })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

export const adminDigitalProductReducer = createSlice({
    name: 'adminDigitalProduct',
    initialState: {
        products: [],
        product: null,
        orders: [],
        totalProducts: 0,
        totalOrders: 0,
        loader: false,
        errorMessage: '',
        successMessage: ''
    },
    reducers: {
        messageClear: (state) => {
            state.errorMessage = ''
            state.successMessage = ''
        }
    },
    extraReducers: (builder) => {
        builder
            // Get products (list)
            .addCase(get_admin_digital_products.pending, (state) => {
                state.loader = true
            })
            .addCase(get_admin_digital_products.fulfilled, (state, { payload }) => {
                state.loader = false
                state.products = payload.products || payload.data || []
                state.totalProducts = paginationTotal(payload, 'totalProducts')
            })
            .addCase(get_admin_digital_products.rejected, (state, { payload }) => {
                state.loader = false
                state.errorMessage = payload?.message
            })
            // Get single product
            .addCase(get_admin_digital_product.pending, (state) => {
                state.loader = true
            })
            .addCase(get_admin_digital_product.fulfilled, (state, { payload }) => {
                state.loader = false
                state.product = payload.product
            })
            .addCase(get_admin_digital_product.rejected, (state, { payload }) => {
                state.loader = false
                state.errorMessage = payload?.message
            })
            // Add product
            .addCase(add_admin_digital_product.pending, (state) => {
                state.loader = true
            })
            .addCase(add_admin_digital_product.fulfilled, (state, { payload }) => {
                state.loader = false
                state.successMessage = payload.message
            })
            .addCase(add_admin_digital_product.rejected, (state, { payload }) => {
                state.loader = false
                state.errorMessage = payload?.message
            })
            // Update product
            .addCase(update_admin_digital_product.pending, (state) => {
                state.loader = true
            })
            .addCase(update_admin_digital_product.fulfilled, (state, { payload }) => {
                state.loader = false
                state.successMessage = payload.message
            })
            .addCase(update_admin_digital_product.rejected, (state, { payload }) => {
                state.loader = false
                state.errorMessage = payload?.message
            })
            // Delete product
            .addCase(delete_admin_digital_product.fulfilled, (state, { payload }) => {
                state.successMessage = payload.message
            })
            // Get orders
            .addCase(get_admin_digital_orders.fulfilled, (state, { payload }) => {
                state.orders = payload.orders || payload.data || []
                state.totalOrders = paginationTotal(payload, 'totalOrders')
            })
            // Update order
            .addCase(update_digital_order_status.fulfilled, (state, { payload }) => {
                state.successMessage = payload.message
            })
    }
})

export const { messageClear } = adminDigitalProductReducer.actions
export default adminDigitalProductReducer.reducer
